<template>
  <div id="app">
    <DataExplorer
      v-if="viewType === 'explorer'"
      :parameters="parameters"
      :computedParameters="computedParameters"
      :defaultParameterValues="parameterValues"
      :indicators="indicators"
      :notes="notes"
    />
    <RegionChart v-else />
  </div>
</template>

<script>
// import DataExplorer from "../../vue-ui/src/components/highcharts/data-explorer/DataExplorer.vue";
import { DataExplorer } from "@international-energy-agency/vue-ui/highcharts";
const apiTestRoute = "https://api.test.iea.org/rdd";
const apiRoute = "https://api.iea.org/rdd";

import RegionChart from "./RegionChart.vue";

export default {
  name: "App",
  components: {
    DataExplorer,
    RegionChart,
  },
  props: {
    viewType: {
      type: String,
      default: "explorer",
      validator: (value) => ["explorer", "static"].includes(value),
    },
    test: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      parameters: [
        {
          id: "COUNTRY",
          name: "Country/Region",
          url: `${this.test ? apiTestRoute : apiRoute}/list/COUNTRY`,
          type: "select",
        },
        {
          id: "PRODUCT",
          name: "Product",
          url: `${this.test ? apiTestRoute : apiRoute}/list/PRODUCT`,
          dynamicParams: ["isRegion"],
          type: "select",
        },
        {
          id: "TECHNOLOGY",
          name: "Technology",
          url: `${this.test ? apiTestRoute : apiRoute}/list/TECHNOLOGY`,
          dynamicParams: ["isRegion"],
          type: "select",
        },
      ],
      computedParameters: [
        {
          id: "isRegion",
          compute: (paramValues, selectedGroups) => {
            return (
              !!selectedGroups["COUNTRY"] &&
              selectedGroups["COUNTRY"] === "Regions"
            );
          },
        },
      ],
      parameterValues: {
        COUNTRY: "IEA",
        PRODUCT: "Total RD&D in million USD (2022 prices and exch. rates)",
        TECHNOLOGY: "Total",
        TIME: "2023",
        isRegion: true,
        share: false,
        type: "total",
      },
      indicators: [
        {
          url: `${this.test ? apiTestRoute : apiRoute}/summary`,
          type: "box",
          params: ["COUNTRY", "PRODUCT", "TIME", "TECHNOLOGY", "isRegion"],
          extraParams: [
            {
              id: "TIME",
              name: "Year",
              url: `${this.test ? apiTestRoute : apiRoute}/list/TIME`,
              type: "select",
            },
          ],
          config: {
            title: (paramValues) => {
              return `Key indicators for ${paramValues.TIME}, ${paramValues.COUNTRY}`;
            },
            indicators: (paramValues, data) => {
              const indicators = {
                "Total budget":
                  data && data.total
                    ? `${Math.round(data.total * 10) / 10} ${this.units[0]}`
                    : "--",
                "Value compared to previous year":
                  data && data.compared
                    ? `${data.compared > 0 ? "Up " : "Down "}${
                        Math.round(Math.abs(data.compared) * 10 * 100) / 10
                      }%`
                    : "--",
              };
              if (
                paramValues &&
                paramValues.PRODUCT &&
                paramValues.PRODUCT.includes("Total")
              ) {
                indicators["RDD per thousand unit of GDP"] =
                  data && data.gdp
                    ? `${Math.round(data.gdp * 100) / 100}`
                    : "--";
              }

              if (data && "tech" in data) {
                indicators["Budget for selected technology"] =
                  data && data.tech
                    ? `${Math.round(data.tech * 10) / 10} ${this.units[0]}`
                    : "--";
              }

              return indicators;
            },
          },
        },
        {
          url: this.test ? apiTestRoute : apiRoute,
          type: "chart",
          params: ["COUNTRY", "PRODUCT", "TECHNOLOGY", "isRegion", "share"],
          extraParams: [
            {
              id: "share",
              type: "buttonGroup",
              options: [
                { label: "Share", value: true },
                { label: "Value", value: false },
              ],
            },
          ],
          config: {
            title: (paramValues) => {
              return `Evolution of budget per year, ${paramValues.TECHNOLOGY}`;
            },
            unit: () => this.units[1],
            chartType: "column",
            xAxisDimension: "TIME",
            yAxisDimension: "TECHNOLOGY",
            valueDimension: "VALUE",
            dataFormatter: (data) => {
              return [...data].sort(
                (a, b) => parseInt(a.ORDER) - parseInt(b.ORDER)
              );
            },
            chartOptions: (paramValues) => ({
              yAxis: {
                reversedStacks: true,
                endOnTick: paramValues.share ? false : true,
              },
              tooltip: {
                valueDecimals: 1,
                shared: true,
                formatter: function (tooltip) {
                  const extendedTooltip = tooltip.defaultFormatter.call(
                    this,
                    tooltip
                  );
                  if (!tooltip.options.valueSuffix.includes("%")) {
                    extendedTooltip[
                      extendedTooltip.length - 1
                    ] = `Total: <b>${this.points[0].total.toFixed(1)}${
                      tooltip.options.valueSuffix
                    }</b>`;
                  }
                  return extendedTooltip;
                },
              },
            }),
            // downloadLink: () => this.downloadLink,
          },
        },
        {
          url: `${this.test ? apiTestRoute : apiRoute}/sum`,
          type: "chart",
          isVisible: (paramValues) =>
            paramValues.PRODUCT &&
            paramValues.PRODUCT.includes("Total") &&
            !paramValues.PRODUCT.includes("national currency"),
          params: ["TIME", "PRODUCT", "type"],
          extraParams: [
            {
              id: "TIME",
              name: "Year",
              url: `${this.test ? apiTestRoute : apiRoute}/list/TIME`,
              type: "select",
            },
            {
              id: "type",
              type: "buttonGroup",
              options: [
                { label: "Total budget", value: "total" },
                { label: "Budget / thousand units of GDP", value: "gdp" },
              ],
            },
          ],
          config: {
            title: (paramValues) => {
              return `Total public energy RD&D budgets ${
                paramValues.type === "gdp" ? "per thousand units of GDP" : ""
              } by country, ${paramValues.TIME}`;
            },
            unit: (paramValues) =>
              paramValues.type === "total" ? this.units[1] : null,
            chartType: "bar",
            xAxisDimension: "COUNTRY",
            yAxisDimension: "TIME",
            valueDimension: "VALUE",
            legendDisabled: true,
            dataFormatter: (data) => {
              const formatted = [...data]
                .filter((e) => !!e.VALUE)
                .sort((a, b) => b.VALUE - a.VALUE);
              return formatted;
            },
            chartOptions: (paramValues) => ({
              chart: {
                height: 900,
              },
              yAxis: {
                reversedStacks: true,
                endOnTick: true,
              },
              tooltip: {
                valueDecimals: paramValues.type === "total" ? 1 : 2,
              },
            }),
          },
          notes:
            "Available data depends on the data submitted by national administration. As a result, the ranking for a given year only shows the countries for which data was submitted.",
        },
      ],
      notes: "NC = National currency",
    };
  },
  computed: {
    units() {
      if (!this.parameterValues.PRODUCT) {
        return [null, null];
      }
      const unit = this.parameterValues.PRODUCT.match(/in ([\w\s]+) \(/)[1];
      return [unit, this.parameterValues.share ? "%" : unit];
    },
    // downloadLink() {
    //   const params = ["COUNTRY", "PRODUCT", "TECHNOLOGY", "isRegion"];
    //   const queryString = params
    //     .map((key) => {
    //       if (
    //         typeof this.parameterValues[key] === "string" &&
    //         this.parameterValues[key].includes(",")
    //       ) {
    //         return (
    //           key + "=" + encodeURIComponent(`"${this.parameterValues[key]}"`)
    //         );
    //       }
    //       return key + "=" + encodeURIComponent(this.parameterValues[key]);
    //     })
    //     .join("&")
    //     .concat("&csv=true");
    //   return `${this.test ? apiTestRoute : apiRoute}?${queryString}`;
    // },
  },
};
</script>
